import useFile from "@/hooks/use-file.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiFile } from "@/types/common.types.ts";
import { LinkButtonTo, Text, ButtonIcon, FontAwesomeIcon, Stack } from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface FileItemProps {
  file: ApiFile;
  onRemove?: () => void;
  onOpenFile?: (file: ApiFile) => Promise<void>;
  removeTitle?: string;
  showMimeType?: boolean;
  showSize?: boolean;
  isOpenDisabled?: boolean;
  className?: string;
  nameClassName?: string;
}

export const FileItem = ({
  file,
  onRemove,
  onOpenFile,
  removeTitle,
  showMimeType = false,
  showSize = true,
  isOpenDisabled = false,
  className,
  nameClassName,
}: FileItemProps) => {
  const { openFileByUUID } = useFile();
  const [isFileLoading, setIsFileLoading] = useState(false);
  const { renderFileSize } = useFormatter();

  const handleOpenFile = useCallback(async () => {
    setIsFileLoading(true);
    if (onOpenFile) {
      await onOpenFile(file);
    } else {
      await openFileByUUID(file.uuid);
    }
    setIsFileLoading(false);
  }, [file, onOpenFile, openFileByUUID]);
  return (
    <Stack
      direction={"horizontal"}
      gap={"sm"}
      justify={"between"}
      items={"center"}
      className={twMerge(
        "bg-dark/5 rounded-xs px-2 py-1 overflow-hidden grow-0 max-w-full",
        className
      )}
    >
      {isOpenDisabled ? (
        <Text
          className={twMerge(
            "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
            nameClassName
          )}
        >
          {file.name}
        </Text>
      ) : (
        <LinkButtonTo
          isLoading={isFileLoading}
          variant={"secondary"}
          className={twMerge(
            "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-brand-dark",
            nameClassName
          )}
          onClick={handleOpenFile}
        >
          {file.name}
        </LinkButtonTo>
      )}
      <Stack direction={"horizontal"} items={"center"} gap={"sm"} className={"shrink-0"}>
        {showMimeType && <Text className="text-secondary">{file.mime_type}</Text>}
        {showSize && <Text className={"shrink-0 text-secondary"}>{renderFileSize(file.size)}</Text>}
        {onRemove && (
          <ButtonIcon
            onClick={onRemove}
            className={"shrink-0"}
            variant={"plain"}
            size={"sm"}
            title={removeTitle}
            icon={<FontAwesomeIcon icon={"fa-light fa-times"} />}
          />
        )}
      </Stack>
    </Stack>
  );
};
